// src/components/MaintenanceActionModal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../services/api';
import CamposExtrasTab from './CamposExtrasTab';

import {
    FaWrench, FaTimesCircle, FaSearch, FaSpinner, FaFilePdf,
    FaExclamationTriangle, FaCog, FaPaperPlane,
    FaHistory, FaFileInvoiceDollar, FaBan, FaComments, FaArrowLeft,
    FaCheckDouble, FaCheck, FaPauseCircle, FaStopCircle, FaCheckCircle,
    FaCheckSquare, FaSquare, FaPaperclip, FaEnvelopeOpenText, FaClock,
    FaLock, FaTimes, FaBell, FaSync, FaDownload, FaBuilding,
    FaShieldAlt, FaExclamationCircle, FaWhatsapp, FaEnvelope,
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const STATUS_ENCERRADOS = ['concluido', 'recusado'];
const POLL_INTERVAL_MS = 20000;

const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const STATUS_LABELS = {
    aguardando_orcamento: 'Em Cotação',
    pendente:   'Suspenso',
    em_analise: 'Em Análise',
    aprovado:   'Em Execução',
    recusado:   'Reprovado',
    concluido:  'Finalizado',
};

// ─── Canal de contato (e-mail | whatsapp) ─────────────────────────────────────
const CANAL_EMAIL    = 'email';
const CANAL_WHATSAPP = 'whatsapp';

// Telefone prioritário de uma oficina (índice 0 do telefones_json). Espelha o backend.
const telefonePrioritario = (oficina) => {
    if (oficina?.telefone) return oficina.telefone;
    let arr = oficina?.telefones_json;
    if (typeof arr === 'string' && arr.trim()) {
        try { arr = JSON.parse(arr); } catch { arr = null; }
    }
    if (Array.isArray(arr) && arr.length) return arr[0];
    return null;
};

// Uma oficina é elegível para o canal se possui o contato correspondente.
const temContatoNoCanal = (oficina, canal) =>
    canal === CANAL_WHATSAPP
        ? !!telefonePrioritario(oficina)
        : !!(oficina?.email && String(oficina.email).trim());

// ─── Gerador de Dossiê ────────────────────────────────────────────────────────
function generateDossieHTML({ ticket, oficina, messages }) {
    const dataGeracao = new Date().toLocaleString('pt-BR');
    const statusLabel = STATUS_LABELS[ticket?.status] || ticket?.status || '---';

    const mensagensHtml = messages.map(msg => {
        const hora = new Date(msg.data_hora).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
        const isSent    = msg.tipo === 'enviada';
        const remetente = isSent ? 'Equipe de Frota' : (oficina?.nome_fantasia || 'Parceiro');
        const cor       = isSent ? '#e0f2fe' : '#f0fdf4';
        const border    = isSent ? '#0369a1' : '#16a34a';
        const anexosHtml = msg.anexos?.length > 0
            ? `<div style="margin-top:6px;">${msg.anexos.map(a =>
                `<span style="background:#f1f5f9;border:1px solid #e2e8f0;border-radius:4px;padding:2px 8px;font-size:11px;margin-right:4px;">📎 ${a.nome}</span>`
              ).join('')}</div>`
            : '';

        return `
        <div style="margin-bottom:14px;display:flex;justify-content:${isSent ? 'flex-end' : 'flex-start'};">
            <div style="max-width:75%;background:${cor};border:1px solid ${border}20;border-radius:10px;padding:10px 14px;border-bottom-${isSent ? 'right' : 'left'}-radius:2px;">
                <div style="font-size:10px;color:#64748b;margin-bottom:4px;font-weight:600;">${remetente}</div>
                <div style="font-size:13px;color:#1e293b;white-space:pre-wrap;line-height:1.5;">${msg.mensagem || ''}</div>
                ${anexosHtml}
                <div style="font-size:10px;color:#94a3b8;margin-top:6px;text-align:right;">${hora}</div>
            </div>
        </div>`;
    }).join('');

    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Dossiê — Manutenção #${ticket?.id}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1e293b; font-size: 13px; }
  @page { margin: 20mm 18mm; }
  @media print { .no-print { display: none !important; } body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  .header { background: #0f172a; color: white; padding: 28px 32px; border-radius: 0 0 12px 12px; margin-bottom: 28px; display: flex; justify-content: space-between; align-items: flex-start; }
  .header h1 { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; }
  .header p  { font-size: 12px; color: rgba(255,255,255,.55); margin-top: 4px; }
  .header-badge { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2); border-radius: 20px; padding: 6px 16px; font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #e2e8f0; white-space: nowrap; }
  .section { margin-bottom: 24px; }
  .section-title { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #64748b; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #f1f5f9; }
  .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  .info-item { background: #f8fafc; border-radius: 8px; padding: 12px; border: 1px solid #e2e8f0; }
  .info-item label { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #94a3b8; display: block; margin-bottom: 4px; }
  .info-item span  { font-size: 13px; font-weight: 600; color: #1e293b; }
  .problem-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 14px; font-size: 13px; color: #78350f; line-height: 1.6; }
  .chat-area { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 20px; min-height: 100px; }
  .empty-chat { color: #94a3b8; text-align: center; padding: 20px; font-style: italic; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #94a3b8; }
  .btn-print { position: fixed; bottom: 24px; right: 24px; background: #0f172a; color: white; border: none; padding: 12px 24px; border-radius: 100px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 8px 24px rgba(0,0,0,.25); display: flex; align-items: center; gap: 8px; font-family: inherit; }
  .btn-print:hover { background: #1e293b; }
</style>
</head>
<body>
<div class="header">
  <div>
    <h1>🔧 Dossiê de Manutenção #${ticket?.id || '---'}</h1>
    <p>Gerado em ${dataGeracao} · BLB System</p>
  </div>
  <span class="header-badge">${statusLabel}</span>
</div>
<div class="section">
  <div class="section-title">Informações da Manutenção</div>
  <div class="info-grid">
    <div class="info-item"><label>Veículo</label><span>${ticket?.veiculo_placa || '---'}</span></div>
    <div class="info-item"><label>Modelo</label><span>${ticket?.veiculo_modelo || '---'}</span></div>
    <div class="info-item"><label>Tipo de Serviço</label><span>${ticket?.tipo_servico || '---'}</span></div>
    <div class="info-item"><label>Status</label><span>${statusLabel}</span></div>
    <div class="info-item"><label>Custo Aprovado</label><span>${formatCurrency(ticket?.valor_final)}</span></div>
    <div class="info-item"><label>Data de Abertura</label><span>${ticket?.data_criacao ? new Date(ticket.data_criacao).toLocaleDateString('pt-BR') : '---'}</span></div>
  </div>
</div>
${ticket?.descricao_problema ? `
<div class="section">
  <div class="section-title">Descrição do Problema</div>
  <div class="problem-box">${ticket.descricao_problema}</div>
</div>` : ''}
<div class="section">
  <div class="section-title">
    Conversa com ${oficina?.nome_fantasia || 'Parceiro'}
    ${oficina?.email ? `<span style="font-weight:400;margin-left:8px;color:#94a3b8;">${oficina.email}</span>` : ''}
    <span style="font-weight:400;margin-left:8px;color:#94a3b8;">(${messages.length} mensagem${messages.length !== 1 ? 's' : ''})</span>
  </div>
  <div class="chat-area">
    ${messages.length === 0 ? '<div class="empty-chat">Nenhuma mensagem nesta conversa.</div>' : mensagensHtml}
  </div>
</div>
<div class="footer">
  <span>BLB System — Gestão de Frota · blbsystem.com.br</span>
  <span>Dossiê gerado em ${dataGeracao}</span>
</div>
<button class="btn-print no-print" onclick="window.print()">🖨️ Imprimir / Salvar PDF</button>
</body>
</html>`;
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const config = {
        aguardando_orcamento: { style: 'warning', label: 'Em Cotação',   icon: <FaSpinner className="go-spin-icon"/> },
        pendente:             { style: 'warning', label: 'Suspenso',     icon: <FaPauseCircle/> },
        em_analise:           { style: 'info',    label: 'Em Análise',   icon: <FaSearch/> },
        aprovado:             { style: 'success', label: 'Em Execução',  icon: <FaWrench/> },
        recusado:             { style: 'danger',  label: 'Reprovado',    icon: <FaStopCircle/> },
        concluido:            { style: 'dark',    label: 'Finalizado',   icon: <FaCheckCircle/> },
        true:                 { style: 'success', label: 'Ativo',        icon: <FaCheckCircle/> },
        false:                { style: 'danger',  label: 'Bloqueado',    icon: <FaBan/> },
        1:                    { style: 'success', label: 'Ativo',        icon: <FaCheckCircle/> },
        0:                    { style: 'danger',  label: 'Bloqueado',    icon: <FaBan/> },
    };
    let key = status;
    if (status === true  || status === 1) key = true;
    if (status === false || status === 0) key = false;
    const current = config[key] || { style: 'default', label: status, icon: null };
    return <span className={`go-status-badge go-badge-${current.style}`}>{current.icon} {current.label}</span>;
};

const EmailTick = ({ status }) => {
    if (status === 'lido')     return <span title="Lido"      style={{ color: '#3b82f6', marginLeft: 5, display: 'inline-flex', alignItems: 'center' }}><FaCheckDouble /></span>;
    if (status === 'entregue') return <span title="Entregue"  style={{ color: '#94a3b8', marginLeft: 5, display: 'inline-flex', alignItems: 'center' }}><FaCheckDouble /></span>;
    return <span title="Enviado" style={{ color: '#cbd5e1', marginLeft: 5, display: 'inline-flex', alignItems: 'center' }}><FaCheck /></span>;
};

const AnexoChip = ({ anexo }) => {
    const isImage = anexo.tipo?.startsWith('image/');
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', fontSize: '0.75rem', color: '#334155', marginTop: 4, marginRight: 4 }}>
            {isImage ? '🖼️' : '📎'} {anexo.nome}
            {anexo.tamanho > 0 && <span style={{ color: '#94a3b8' }}>({(anexo.tamanho / 1024).toFixed(0)} KB)</span>}
        </div>
    );
};

// ─── WIZARD DE APROVAÇÃO (3 etapas) ──────────────────────────────────────────
function ApprovalWizard({ ticket, oficinas, onClose, onConfirmed }) {
    const [step,             setStep]             = useState(1);
    const [selectedOficina,  setSelectedOficina]  = useState(null);
    const [valorInput,       setValorInput]        = useState(ticket?.valor_final ? String(ticket.valor_final) : '');
    const [searchOficina,    setSearchOficina]     = useState('');
    const [confirming,       setConfirming]        = useState(false);

    const ativasFiltered = (oficinas || [])
        .filter(o => o.ativo === 1 || o.ativo === true)
        .filter(o => o.nome_fantasia.toLowerCase().includes(searchOficina.toLowerCase()));

    const canAdvanceStep1 = selectedOficina && valorInput && parseFloat(valorInput) > 0;

    const handleFinalConfirm = async () => {
        setConfirming(true);
        try {
            await onConfirmed({
                valor_final:   parseFloat(valorInput),
                fornecedor_id: selectedOficina?.id || null,
            });
        } catch {
            setConfirming(false);
        }
    };

    return (
        <div className="apw-backdrop" onClick={onClose}>
            <div className="apw-card" onClick={e => e.stopPropagation()}>

                {/* ── Progress tracker ── */}
                <div className="apw-progress-bar">
                    {[1, 2, 3].map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`apw-step-dot ${step > s ? 'done' : step === s ? 'active' : ''}`}>
                                {step > s ? <FaCheck size={9} /> : s}
                            </div>
                            {i < 2 && <div className={`apw-step-line ${step > s ? 'done' : ''}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {/* ════════════════════════════════════════
                    ETAPA 1 — Selecionar Fornecedor + Valor
                    ════════════════════════════════════════ */}
                {step === 1 && (
                    <>
                        <div className="apw-header">
                            <div className="apw-header-icon blue">
                                <FaCheckCircle />
                            </div>
                            <div>
                                <h3>Aprovar Orçamento</h3>
                                <p>Selecione o fornecedor e informe o valor acordado</p>
                            </div>
                        </div>

                        <div className="apw-body">
                            <label className="apw-field-label">
                                <FaBuilding size={11} /> Fornecedor que executará o serviço
                            </label>

                            {/* Busca de oficina */}
                            <div className="apw-search-box">
                                <FaSearch />
                                <input
                                    type="text"
                                    placeholder="Buscar parceiro..."
                                    value={searchOficina}
                                    onChange={e => setSearchOficina(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="apw-oficinas-list">
                                {ativasFiltered.length === 0 && (
                                    <div className="apw-oficinas-empty">Nenhum parceiro encontrado.</div>
                                )}
                                {ativasFiltered.map(o => (
                                    <div
                                        key={o.id}
                                        className={`apw-oficina-item ${selectedOficina?.id === o.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedOficina(o)}
                                    >
                                        <div className="apw-radio-btn">
                                            {selectedOficina?.id === o.id && <div className="apw-radio-dot" />}
                                        </div>
                                        <div className="apw-oficina-info">
                                            <strong>{o.nome_fantasia}</strong>
                                            {o.especialidade && <span>{o.especialidade}</span>}
                                        </div>
                                        {selectedOficina?.id === o.id && (
                                            <FaCheckCircle style={{ color: '#2563eb', flexShrink: 0, fontSize: '0.85rem' }} />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <label className="apw-field-label" style={{ marginTop: 16 }}>
                                Valor final aprovado
                            </label>
                            <div className="apw-valor-input">
                                <span className="apw-valor-prefix">R$</span>
                                <input
                                    type="number"
                                    placeholder="0,00"
                                    min="0"
                                    step="0.01"
                                    value={valorInput}
                                    onChange={e => setValorInput(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="apw-footer">
                            <button className="apw-btn ghost" onClick={onClose}>Cancelar</button>
                            <button
                                className="apw-btn primary"
                                disabled={!canAdvanceStep1}
                                onClick={() => setStep(2)}
                            >
                                Próximo <span style={{ fontSize: '0.85rem' }}>→</span>
                            </button>
                        </div>
                    </>
                )}

                {/* ════════════════════════════════════════
                    ETAPA 2 — Primeira Confirmação
                    ════════════════════════════════════════ */}
                {step === 2 && (
                    <>
                        <div className="apw-header">
                            <div className="apw-header-icon amber">
                                <FaExclamationTriangle />
                            </div>
                            <div>
                                <h3>Confirmar Aprovação</h3>
                                <p>Revise os dados antes de continuar</p>
                            </div>
                        </div>

                        <div className="apw-body">
                            <div className="apw-summary-card">
                                <div className="apw-summary-row">
                                    <span>Veículo</span>
                                    <strong>{ticket.veiculo_placa} — {ticket.veiculo_modelo}</strong>
                                </div>
                                <div className="apw-summary-row">
                                    <span>Serviço</span>
                                    <strong>{ticket.tipo_servico}</strong>
                                </div>
                                <div className="apw-summary-row">
                                    <span>Fornecedor selecionado</span>
                                    <strong style={{ color: '#2563eb' }}>{selectedOficina?.nome_fantasia}</strong>
                                </div>
                                <div className="apw-summary-row highlight">
                                    <span>Valor aprovado</span>
                                    <strong>{formatCurrency(valorInput)}</strong>
                                </div>
                            </div>

                            <div className="apw-alert amber">
                                <FaExclamationCircle style={{ flexShrink: 0, marginTop: 2 }} />
                                <p>
                                    Você está prestes a aprovar este orçamento e vincular{' '}
                                    <strong>{selectedOficina?.nome_fantasia}</strong> como fornecedor desta manutenção.
                                    Tem certeza que é isso?
                                </p>
                            </div>
                        </div>

                        <div className="apw-footer">
                            <button className="apw-btn ghost" onClick={() => setStep(1)}>← Voltar</button>
                            <button className="apw-btn amber" onClick={() => setStep(3)}>
                                Sim, estou certo <span style={{ fontSize: '0.85rem' }}>→</span>
                            </button>
                        </div>
                    </>
                )}

                {/* ════════════════════════════════════════
                    ETAPA 3 — Confirmação Final (irreversível)
                    ════════════════════════════════════════ */}
                {step === 3 && (
                    <>
                        <div className="apw-header danger-bg">
                            <div className="apw-header-icon red">
                                <FaShieldAlt />
                            </div>
                            <div>
                                <h3 style={{ color: '#7f1d1d' }}>Confirmação Final</h3>
                                <p style={{ color: '#991b1b' }}>Esta ação <strong>não pode ser desfeita</strong></p>
                            </div>
                        </div>

                        <div className="apw-body">
                            <div className="apw-final-box">
                                <div className="apw-final-title">
                                    Ao confirmar, você irá:
                                </div>
                                <ul className="apw-final-list">
                                    <li>
                                        <span className="apw-check-icon">✓</span>
                                        Aprovar o orçamento de{' '}
                                        <strong>{formatCurrency(valorInput)}</strong>
                                    </li>
                                    <li>
                                        <span className="apw-check-icon">✓</span>
                                        Vincular <strong>{selectedOficina?.nome_fantasia}</strong> à manutenção
                                    </li>
                                    <li>
                                        <span className="apw-check-icon">✓</span>
                                        Alterar status para <strong>Em Execução</strong>
                                    </li>
                                </ul>
                            </div>

                            <div className="apw-alert danger">
                                <FaLock style={{ flexShrink: 0, marginTop: 2 }} />
                                <p>
                                    Esta é sua última chance de cancelar. Após aprovado,
                                    o orçamento fica registrado e a OS entra em execução.
                                </p>
                            </div>
                        </div>

                        <div className="apw-footer danger-footer">
                            <button
                                className="apw-btn ghost"
                                onClick={() => setStep(2)}
                                disabled={confirming}
                            >
                                ← Voltar
                            </button>
                            <button
                                className="apw-btn danger"
                                onClick={handleFinalConfirm}
                                disabled={confirming}
                            >
                                {confirming
                                    ? <><FaSpinner className="go-spin-icon" /> Aprovando...</>
                                    : <><FaCheckDouble /> Aprovar Definitivamente</>
                                }
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ─── MODAL PRINCIPAL ──────────────────────────────────────────────────────────
export default function MaintenanceActionModal({ ticket, onClose, onUpdate, allOficinas }) {
    const [activeTab, setActiveTab] = useState('quotes');

    const [selectedProviders, setSelectedProviders] = useState([]);
    const [filterProvider,    setFilterProvider]    = useState('');
    // Canal ativo da aba Cotações & Conversas: 'email' (padrão) | 'whatsapp'
    const [canal,             setCanal]             = useState(CANAL_EMAIL);
    const [activeChatId,      setActiveChatId]      = useState(null);
    const [chatMessages,      setChatMessages]      = useState([]);
    const [loadingChat,       setLoadingChat]       = useState(false);
    const [refreshingChat,    setRefreshingChat]    = useState(false);
    const [lastUpdated,       setLastUpdated]       = useState(null);
    const [unreadByOficina,   setUnreadByOficina]   = useState({});
    const [newMessageFlash,   setNewMessageFlash]   = useState(null);

    const [emailSubject,     setEmailSubject]     = useState('');
    const [emailBody,        setEmailBody]        = useState('');
    const [emailAttachments, setEmailAttachments] = useState([]);
    const [attachHistory,    setAttachHistory]    = useState(false);
    const [sending,          setSending]          = useState(false);

    const [replyMessage,    setReplyMessage]    = useState('');
    const [chatAttachments, setChatAttachments] = useState([]);
    const [sendingReply,    setSendingReply]    = useState(false);

    // ── NF Upload state ──
    const [nfArquivo, setNfArquivo] = useState(null);

    // ── Approval wizard state ──
    const [approvalWizardOpen, setApprovalWizardOpen] = useState(false);

    const [logsAuditoria, setLogsAuditoria] = useState([]);
    const [loadingLogs,   setLoadingLogs]   = useState(false);

    const fileInputRef     = useRef(null);
    const chatFileInputRef = useRef(null);
    const nfFileInputRef   = useRef(null);
    const chatScrollRef    = useRef(null);
    const socketRef        = useRef(null);
    const activeChatIdRef  = useRef(null);
    const canalRef         = useRef(CANAL_EMAIL);

    const isClosed = STATUS_ENCERRADOS.includes(ticket?.status);

    useEffect(() => { activeChatIdRef.current = activeChatId; }, [activeChatId]);
    useEffect(() => { canalRef.current = canal; }, [canal]);

    useEffect(() => {
        if (ticket) {
            setEmailSubject(`Solicitação de Orçamento - Veículo: ${ticket.veiculo_modelo} (${ticket.veiculo_placa})`);
            setEmailBody(`Olá parceiro,\n\nGostaria de solicitar um orçamento para manutenção do veículo ${ticket.veiculo_modelo}, placa ${ticket.veiculo_placa}.\n\nDescrição do Problema:\n${ticket.descricao_problema || 'Verificação geral necessária.'}\n\nFavor responder este e-mail com os valores e prazo.\n\nAtenciosamente,\nEquipe de Frota`);
        }
    }, [ticket]);

    // ── Fetch mensagens ───────────────────────────────────────────────────────
    const fetchChatMessages = useCallback(async (oficinaId, silent = false) => {
        if (!ticket?.id || !oficinaId) return;
        if (!silent) setLoadingChat(true);
        try {
            const res = await api.get(`/api/frota/manutencoes/${ticket.id}/chat/${oficinaId}?canal=${canal}`);
            const newMsgs = res.data;
            setChatMessages(prev => {
                const novasRecebidas = newMsgs.filter(m => m.tipo === 'recebida' && !prev.some(p => p.id === m.id));
                if (novasRecebidas.length > 0 && prev.length > 0) {
                    setNewMessageFlash(oficinaId);
                    setTimeout(() => setNewMessageFlash(null), 3000);
                    setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 100);
                }
                return newMsgs;
            });
            setLastUpdated(new Date());
        } catch (error) {
            if (!silent) { console.error('Erro ao carregar conversa:', error); toast.error('Não foi possível carregar as mensagens.'); }
        } finally {
            if (!silent) setLoadingChat(false);
        }
    }, [ticket?.id, canal]);

    const fetchUnreadCounts = useCallback(async () => {
        if (!ticket?.id) return;
        try {
            const res = await api.get(`/api/frota/manutencoes/${ticket.id}/unread-counts`);
            if (res.data && typeof res.data === 'object') {
                setUnreadByOficina(prev => {
                    const updated = { ...prev };
                    Object.entries(res.data).forEach(([id, count]) => {
                        const numId = parseInt(id);
                        if (numId !== activeChatIdRef.current) {
                            if ((updated[numId] || 0) < count) {
                                setNewMessageFlash(numId);
                                setTimeout(() => setNewMessageFlash(null), 3000);
                            }
                            updated[numId] = count;
                        }
                    });
                    return updated;
                });
            }
        } catch {}
    }, [ticket?.id]);

    useEffect(() => {
        if (!ticket?.id) return;
        fetchUnreadCounts();
        const interval = setInterval(() => {
            if (activeChatIdRef.current) fetchChatMessages(activeChatIdRef.current, true);
            fetchUnreadCounts();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [ticket?.id, fetchChatMessages, fetchUnreadCounts]);

    useEffect(() => {
        if (!ticket?.id) return;
        const token  = localStorage.getItem('token');
        const socket = io(API_BASE, { transports: ['websocket'], auth: { token } });
        socketRef.current = socket;

        socket.on('nova_resposta_oficina', (data) => {
            if (data.manutencao_id !== ticket.id) return;
            const mesmoCanal = !data.canal || data.canal === canalRef.current;
            if (activeChatIdRef.current === data.oficina_id && mesmoCanal) {
                fetchChatMessages(data.oficina_id, true);
            } else {
                setUnreadByOficina(prev => ({ ...prev, [data.oficina_id]: (prev[data.oficina_id] || 0) + 1 }));
                setNewMessageFlash(data.oficina_id);
                setTimeout(() => setNewMessageFlash(null), 4000);
                const nomeOficina = (allOficinas || []).find(o => o.id === data.oficina_id)?.nome_fantasia || 'Parceiro';
                const viaCanal = data.canal === CANAL_WHATSAPP ? 'WhatsApp' : 'e-mail';
                toast.info(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {data.canal === CANAL_WHATSAPP
                            ? <FaWhatsapp style={{ color: '#16a34a', flexShrink: 0 }} />
                            : <FaBell style={{ color: '#f59e0b', flexShrink: 0 }} />}
                        <div><strong>{nomeOficina}</strong><br /><small>respondeu a cotação por {viaCanal}!</small></div>
                    </div>,
                    { autoClose: 5000 }
                );
            }
        });

        socket.on('email_evento', (data) => {
            if (data.manutencao_id !== ticket.id) return;
            setChatMessages(prev => prev.map(msg => msg.id === data.conversa_id ? { ...msg, status_leitura: data.status } : msg));
        });

        return () => { socket.off('nova_resposta_oficina'); socket.off('email_evento'); socket.disconnect(); };
    }, [ticket?.id, allOficinas, fetchChatMessages]);

    useEffect(() => {
        if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }, [chatMessages]);

    useEffect(() => {
        if (activeChatId) {
            fetchChatMessages(activeChatId);
            setUnreadByOficina(prev => ({ ...prev, [activeChatId]: 0 }));
            api.post(`/api/frota/manutencoes/${ticket.id}/chat/${activeChatId}/mark-read`, { canal }).catch(() => {});
        } else {
            setChatMessages([]);
            setLastUpdated(null);
        }
    }, [activeChatId, fetchChatMessages, ticket?.id, canal]);

    useEffect(() => {
        if (activeTab !== 'logs' || !ticket?.id) return;
        const fetchLogs = async () => {
            setLoadingLogs(true);
            try {
                const res = await api.get(`/api/frota/manutencoes/${ticket.id}/logs`);
                setLogsAuditoria(res.data);
            } catch { toast.error('Não foi possível carregar o histórico.'); }
            finally { setLoadingLogs(false); }
        };
        fetchLogs();
    }, [activeTab, ticket?.id]);

    if (!ticket) return null;

    const safeOficinas     = Array.isArray(allOficinas) ? allOficinas : [];
    const filteredOficinas = safeOficinas.filter(o =>
        o.nome_fantasia.toLowerCase().includes(filterProvider.toLowerCase()) &&
        (o.ativo === true || o.ativo === 1)
    );
    // Oficinas (já filtradas pela busca) que podem ser contatadas no canal ativo.
    const elegiveisFiltradas = filteredOficinas.filter(o => temContatoNoCanal(o, canal));
    const semContatoQtd      = filteredOficinas.length - elegiveisFiltradas.length;
    const isAllSelected    = elegiveisFiltradas.length > 0 && elegiveisFiltradas.every(o => selectedProviders.includes(o.id));
    const activeOfficeData = safeOficinas.find(o => o.id === activeChatId);
    const totalUnread      = Object.values(unreadByOficina).reduce((a, b) => a + b, 0);

    const toggleProvider  = (id) => setSelectedProviders(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleSelectAll = () => isAllSelected ? setSelectedProviders([]) : setSelectedProviders(elegiveisFiltradas.map(o => o.id));

    // Troca de canal: limpa seleções inelegíveis e (se houver chat aberto) refetch é automático via deps.
    const handleCanalChange = (novoCanal) => {
        if (novoCanal === canal) return;
        setCanal(novoCanal);
        setSelectedProviders(prev => prev.filter(id => {
            const o = safeOficinas.find(x => x.id === id);
            return o && temContatoNoCanal(o, novoCanal);
        }));
    };

    const handleManualRefresh = async () => {
        setRefreshingChat(true);
        await fetchChatMessages(activeChatId);
        setRefreshingChat(false);
    };

    const handleDownloadDossie = () => {
        const html = generateDossieHTML({ ticket, oficina: activeOfficeData, messages: chatMessages });
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url  = URL.createObjectURL(blob);
        const win  = window.open(url, '_blank');
        if (!win) toast.warning('Popup bloqueado. Permita popups e tente novamente.');
        setTimeout(() => URL.revokeObjectURL(url), 15000);
    };

    const handleBulkQuote = async () => {
        if (selectedProviders.length === 0) return toast.warning('Selecione pelo menos um parceiro.');
        if (isClosed) return toast.error('Cotação encerrada.');

        const isWhats = canal === CANAL_WHATSAPP;
        if (isWhats) {
            if (!emailBody.trim()) return toast.warning('A mensagem é obrigatória.');
        } else if (!emailSubject || !emailBody) {
            return toast.warning('Assunto e mensagem são obrigatórios.');
        }

        setSending(true);
        try {
            const formData = new FormData();
            formData.append('canal', canal);
            formData.append('oficinas_ids', JSON.stringify(selectedProviders));
            formData.append('message', emailBody);
            // Assunto: no WhatsApp serve só de rótulo interno; no e-mail vai no header.
            formData.append('subject', emailSubject || 'Solicitação de Cotação');
            if (!isWhats) {
                formData.append('attach_history_pdf', attachHistory);
                emailAttachments.forEach(file => formData.append('attachments', file));
            }
            const response = await api.post(`/api/frota/manutencoes/${ticket.id}/solicitar-cotacao`, formData);
            toast.success(response.data.message || 'Cotações enviadas!');
            setSelectedProviders([]);
            setEmailAttachments([]);
            setAttachHistory(false);
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Erro ao enviar cotações.');
        } finally {
            setSending(false);
        }
    };

    const handleSendIndividualReply = async () => {
        const isWhats = canal === CANAL_WHATSAPP;
        if (isWhats && !replyMessage.trim()) return toast.warning('Digite uma mensagem para enviar pelo WhatsApp.');
        if (!replyMessage.trim() && chatAttachments.length === 0) return;
        if (isClosed) return toast.error('Cotação encerrada.');
        setSendingReply(true);
        try {
            const formData = new FormData();
            formData.append('canal', canal);
            formData.append('mensagem', replyMessage);
            if (!isWhats) chatAttachments.forEach(file => formData.append('attachments', file));
            const res = await api.post(`/api/frota/manutencoes/${ticket.id}/chat/${activeChatId}`, formData);
            const novaMsg = { id: res.data.id || Date.now(), tipo: 'enviada', canal, mensagem: replyMessage, data_hora: new Date().toISOString(), status_leitura: 'enviado', anexos: isWhats ? [] : chatAttachments.map(f => ({ nome: f.name, tipo: f.type, tamanho: f.size })) };
            setChatMessages(prev => [...prev, novaMsg]);
            setReplyMessage('');
            setChatAttachments([]);
            setLastUpdated(new Date());
        } catch (error) {
            toast.error(error.response?.data?.error || 'Erro ao enviar mensagem.');
        } finally {
            setSendingReply(false);
        }
    };

    // ── Decisões ──────────────────────────────────────────────────────────────

    // ✨ Abre wizard em vez de aprovar direto
    const handleOpenApprovalWizard = () => {
        if (isClosed) return toast.error('Cotação encerrada.');
        setApprovalWizardOpen(true);
    };

    // Chamado pelo wizard após dupla confirmação
    const handleApproveConfirmed = async ({ valor_final, fornecedor_id }) => {
        try {
            await api.put(`/api/frota/manutencoes/${ticket.id}/aprovar`, {
                valor_final,
                fornecedor_id: fornecedor_id || null,
            });
            toast.success('✅ Orçamento aprovado com sucesso!');
            setApprovalWizardOpen(false);
            onUpdate();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao aprovar.');
            throw err; // propaga para o wizard tratar o loading
        }
    };

    const handlePending = async () => {
        try {
            await api.put(`/api/frota/manutencoes/${ticket.id}/pendente`, {});
            toast.warning('Status alterado para Pendente.');
            onUpdate(); onClose();
        } catch { toast.error('Erro ao alterar status.'); }
    };

    const handleAnalyze = async () => {
        try {
            await api.put(`/api/frota/manutencoes/${ticket.id}/analise`, {});
            toast.info('Status alterado para Em Análise.');
            onUpdate(); onClose();
        } catch { toast.error('Erro ao alterar status.'); }
    };

    const handleConclude = async () => {
        if (!nfArquivo) return toast.warning('Anexe a Nota Fiscal para concluir.');
        const formData = new FormData();
        formData.append('nota_fiscal', nfArquivo);
        try {
            await api.post(`/api/frota/manutencoes/${ticket.id}/concluir`, formData);
            toast.success('Manutenção finalizada!');
            onUpdate(); onClose();
        } catch { toast.error('Erro ao concluir.'); }
    };

    const handleReject = async () => {
        if (!window.confirm('Reprovar esta manutenção?')) return;
        try {
            await api.put(`/api/frota/manutencoes/${ticket.id}/reprovar`, {});
            toast.info('Manutenção reprovada.');
            onUpdate(); onClose();
        } catch { toast.error('Erro ao reprovar.'); }
    };

    return (
        <>
            <div className="go-modal-backdrop">
                <div className="go-modal-window super-modal">

                    {/* HEADER */}
                    <div className="cockpit-header">
                        <div className="cockpit-title">
                            <h2><FaWrench /> Manutenção #{ticket.id}</h2>
                            <div className="cockpit-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {ticket.veiculo_placa} — {ticket.veiculo_modelo} &nbsp;|&nbsp; Status:
                                <StatusBadge status={ticket.status} />
                                {isClosed && (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#ef4444', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 20, padding: '2px 10px' }}>
                                        <FaLock size={10} /> Cotação encerrada
                                    </span>
                                )}
                            </div>
                        </div>
                        <button className="go-close-btn light" onClick={onClose}><FaTimesCircle size={28}/></button>
                    </div>

                    {/* TABS */}
                    <div className="cockpit-tabs">
                        <button className={`cockpit-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}><FaSearch /> Visão Geral</button>
                        <button className={`cockpit-tab ${activeTab === 'quotes'   ? 'active' : ''}`} onClick={() => setActiveTab('quotes')}>
                            <FaComments /> Cotações & Conversas
                            {totalUnread > 0 && <span className="mam-unread-tab-badge">{totalUnread}</span>}
                        </button>
                        <button className={`cockpit-tab ${activeTab === 'actions' ? 'active' : ''}`} onClick={() => setActiveTab('actions')}><FaCog /> Decisões</button>
                        <button className={`cockpit-tab ${activeTab === 'campos_extras' ? 'active' : ''}`} onClick={() => setActiveTab('campos_extras')}><FaWrench /> Campos Extras</button>
                        <button className={`cockpit-tab ${activeTab === 'logs'    ? 'active' : ''}`} onClick={() => setActiveTab('logs')}><FaHistory /> Auditoria & Logs</button>
                    </div>

                    <div className="cockpit-body-wrapper">

                        {/* ── VISÃO GERAL ── */}
                        {activeTab === 'overview' && (
                            <div className="cockpit-content go-animate-up">
                                <div className="details-grid">
                                    <div className="detail-item"><label>Veículo</label><p>{ticket.veiculo_placa}</p></div>
                                    <div className="detail-item"><label>Modelo</label><p>{ticket.veiculo_modelo}</p></div>
                                    <div className="detail-item"><label>Data Abertura</label><p>{new Date(ticket.data_criacao || Date.now()).toLocaleDateString()}</p></div>
                                    <div className="detail-item full-width" style={{ gridColumn: 'span 3' }}>
                                        <label>Descrição do Problema</label>
                                        <p className="problem-text">{ticket.descricao_problema || 'Sem descrição.'}</p>
                                    </div>
                                    <div className="detail-item"><label>Tipo de Serviço</label><p>{ticket.tipo_servico}</p></div>
                                    <div className="detail-item"><label>Custo Aprovado</label><p style={{ color: 'var(--go-primary)', fontWeight: 'bold' }}>{formatCurrency(ticket.valor_final)}</p></div>
                                </div>
                                <div className="info-alert-box">
                                    <FaExclamationTriangle />
                                    <p>Verifique o histórico deste veículo antes de aprovar novos gastos.</p>
                                </div>
                            </div>
                        )}

                        {/* ── COTAÇÕES & CONVERSAS ── */}
                        {activeTab === 'quotes' && (
                            <div className="quote-layout-split go-animate-up">
                                {/* SIDEBAR */}
                                <div className="quote-sidebar-list">
                                    <div className="qs-header">
                                        {/* Sub-tabs de canal — E-mail | WhatsApp */}
                                        <div className="mam-canal-switch" role="tablist" aria-label="Canal de contato">
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={canal === CANAL_EMAIL}
                                                className={`mam-canal-pill ${canal === CANAL_EMAIL ? 'active email' : ''}`}
                                                onClick={() => handleCanalChange(CANAL_EMAIL)}
                                            >
                                                <FaEnvelope /> E-mail
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={canal === CANAL_WHATSAPP}
                                                className={`mam-canal-pill ${canal === CANAL_WHATSAPP ? 'active whatsapp' : ''}`}
                                                onClick={() => handleCanalChange(CANAL_WHATSAPP)}
                                            >
                                                <FaWhatsapp /> WhatsApp
                                            </button>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <h4 style={{ margin: 0 }}>Comunicações</h4>
                                            {totalUnread > 0 && (
                                                <span style={{ background: '#ef4444', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, animation: 'mam-pulse 1.5s infinite' }}>
                                                    <FaBell size={10} /> {totalUnread} novo{totalUnread > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="qs-search">
                                            <FaSearch />
                                            <input placeholder="Buscar oficina..." value={filterProvider} onChange={e => setFilterProvider(e.target.value)} />
                                        </div>
                                        <div className="qs-select-all" onClick={toggleSelectAll}>
                                            {isAllSelected ? <FaCheckSquare className="active"/> : <FaSquare className="inactive"/>}
                                            <span>Marcar Todos ({elegiveisFiltradas.length})</span>
                                        </div>
                                        {semContatoQtd > 0 && (
                                            <div className="mam-canal-warn">
                                                <FaExclamationTriangle />
                                                <span>
                                                    <strong>{semContatoQtd}</strong> sem {canal === CANAL_WHATSAPP ? 'telefone' : 'e-mail'} — não disparáveis por {canal === CANAL_WHATSAPP ? 'WhatsApp' : 'e-mail'}.
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="qs-list">
                                        {filteredOficinas.map(oficina => {
                                            const unread    = unreadByOficina[oficina.id] || 0;
                                            const isFlash   = newMessageFlash === oficina.id;
                                            const isActive  = activeChatId === oficina.id;
                                            const elegivel  = temContatoNoCanal(oficina, canal);
                                            const contato   = canal === CANAL_WHATSAPP ? telefonePrioritario(oficina) : oficina.email;
                                            return (
                                                <div key={oficina.id} className={`qs-item ${isActive ? 'active-chat' : ''} ${unread > 0 ? 'has-unread' : ''} ${isFlash ? 'new-message-flash' : ''} ${!elegivel ? 'mam-qs-disabled' : ''}`}>
                                                    <div
                                                        className="qs-checkbox"
                                                        onClick={e => { e.stopPropagation(); if (elegivel) toggleProvider(oficina.id); }}
                                                        title={elegivel ? '' : `Sem ${canal === CANAL_WHATSAPP ? 'telefone' : 'e-mail'} cadastrado`}
                                                    >
                                                        {selectedProviders.includes(oficina.id) ? <FaCheckSquare className="txt-highlight"/> : <FaSquare/>}
                                                    </div>
                                                    <div className="qs-info" style={{ cursor: 'pointer', flex: 1 }} onClick={() => setActiveChatId(oficina.id)}>
                                                        <strong style={{ color: isActive ? 'var(--go-primary)' : unread > 0 ? '#0f172a' : 'inherit', fontWeight: unread > 0 ? 700 : 600 }}>
                                                            {oficina.nome_fantasia}
                                                        </strong>
                                                        <small style={{ color: unread > 0 ? '#64748b' : '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            {unread > 0
                                                                ? `${unread} mensagem${unread > 1 ? 's' : ''} não lida${unread > 1 ? 's' : ''}`
                                                                : elegivel
                                                                    ? <>{canal === CANAL_WHATSAPP ? <FaWhatsapp style={{ color: '#16a34a' }} /> : <FaEnvelope style={{ color: '#64748b' }} />} {contato}</>
                                                                    : <span style={{ color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 4 }}><FaExclamationTriangle size={10} /> sem {canal === CANAL_WHATSAPP ? 'telefone' : 'e-mail'}</span>
                                                            }
                                                        </small>
                                                    </div>
                                                    {unread > 0 && <span className="mam-unread-badge">{unread > 99 ? '99+' : unread}</span>}
                                                </div>
                                            );
                                        })}
                                        {filteredOficinas.length === 0 && <p className="qs-empty">Nenhuma oficina disponível.</p>}
                                    </div>

                                    <div className="qs-footer-info">
                                        <strong>{selectedProviders.length}</strong> selecionados para disparo
                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginTop: 2 }}>Polling a cada {POLL_INTERVAL_MS / 1000}s</span>
                                    </div>
                                </div>

                                {/* DIREITA */}
                                <div className="quote-main-composer">
                                    {!activeChatId ? (
                                        <div className="composer-card go-animate-up">
                                            <div className="composer-header">
                                                <h3>
                                                    {canal === CANAL_WHATSAPP ? <FaWhatsapp style={{ color: '#16a34a' }} /> : <FaPaperPlane />}
                                                    {' '}Nova Solicitação
                                                </h3>
                                                <span className="composer-badge" style={canal === CANAL_WHATSAPP ? { background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' } : undefined}>
                                                    {canal === CANAL_WHATSAPP ? 'WhatsApp · Disparo em Massa' : 'E-mail · Disparo em Massa'}
                                                </span>
                                            </div>
                                            {isClosed && (
                                                <div style={{ margin: '0 0 12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', fontSize: '0.85rem' }}>
                                                    <FaLock /> Cotação encerrada — novos disparos bloqueados.
                                                </div>
                                            )}
                                            <div className="composer-body">
                                                {canal === CANAL_EMAIL && (
                                                    <div className="composer-field">
                                                        <label>Assunto</label>
                                                        <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} disabled={isClosed} />
                                                    </div>
                                                )}
                                                <div className="composer-field">
                                                    <label>{canal === CANAL_WHATSAPP ? 'Mensagem do WhatsApp' : 'Mensagem Base'}</label>
                                                    <textarea rows={6} value={emailBody} onChange={e => setEmailBody(e.target.value)} disabled={isClosed} />
                                                    {canal === CANAL_WHATSAPP && (
                                                        <small style={{ color: '#94a3b8', marginTop: 6, display: 'block' }}>
                                                            Texto puro (sem anexos). Use <code>*texto*</code> para negrito. O “Olá parceiro” é trocado pelo nome de cada oficina.
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="composer-actions">
                                                    {canal === CANAL_EMAIL ? (
                                                        <div className="composer-attachments">
                                                            <input type="file" multiple ref={fileInputRef} style={{ display: 'none' }} onChange={e => {
                                                                if (e.target.files?.length) { setEmailAttachments(prev => [...prev, ...Array.from(e.target.files)]); toast.info(`${e.target.files.length} arquivo(s) adicionado(s).`); }
                                                            }}/>
                                                            <button className={`btn-attach ${emailAttachments.length > 0 ? 'active' : ''}`} onClick={() => fileInputRef.current.click()} disabled={isClosed}>
                                                                <FaPaperclip /> {emailAttachments.length > 0 ? `${emailAttachments.length} Anexo(s)` : 'Anexar Fotos'}
                                                            </button>
                                                            <button className={`btn-attach ${attachHistory ? 'active' : ''}`} onClick={() => setAttachHistory(!attachHistory)} disabled={isClosed}>
                                                                {attachHistory ? <FaCheckSquare /> : <FaFilePdf />} Histórico
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="composer-attachments">
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 12px' }}>
                                                                <FaWhatsapp /> Envio via Z-API ao telefone prioritário
                                                            </span>
                                                        </div>
                                                    )}
                                                    <button
                                                        className="btn-send-mass"
                                                        onClick={handleBulkQuote}
                                                        disabled={sending || selectedProviders.length === 0 || isClosed}
                                                        style={canal === CANAL_WHATSAPP ? { background: '#16a34a' } : undefined}
                                                    >
                                                        {sending ? <FaSpinner className="go-spin-icon"/> : (
                                                            canal === CANAL_WHATSAPP
                                                                ? <><FaWhatsapp /> Disparar WhatsApp ({selectedProviders.length})</>
                                                                : <><FaEnvelopeOpenText /> Disparar Emails ({selectedProviders.length})</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="chat-view-container go-animate-up">
                                            <div className="chat-header">
                                                <button className="go-btn-new ghost sm" onClick={() => setActiveChatId(null)}><FaArrowLeft /> Voltar</button>
                                                <div className="chat-office-info">
                                                    <strong>{activeOfficeData?.nome_fantasia}</strong>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                        {canal === CANAL_WHATSAPP
                                                            ? <><FaWhatsapp style={{ color: '#16a34a' }} /> {telefonePrioritario(activeOfficeData) || 'sem telefone'}</>
                                                            : <><FaEnvelope style={{ color: '#64748b' }} /> {activeOfficeData?.email || 'sem e-mail'}</>}
                                                    </span>
                                                </div>
                                                {lastUpdated && (
                                                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                                        ↻ {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </span>
                                                )}
                                                <button className="go-btn-new outline sm" onClick={handleDownloadDossie} title="Baixar dossiê da conversa" style={{ flexShrink: 0 }}>
                                                    <FaDownload style={{ fontSize: '0.8rem' }} /> Dossiê
                                                </button>
                                                <button className="go-btn-new outline sm" onClick={handleManualRefresh} disabled={refreshingChat} title="Atualizar conversa" style={{ flexShrink: 0 }}>
                                                    <FaSync style={{ fontSize: '0.8rem', animation: refreshingChat ? 'go-spin 0.7s linear infinite' : 'none' }} />
                                                    {refreshingChat ? 'Buscando...' : 'Atualizar'}
                                                </button>
                                                {isClosed && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>
                                                        <FaLock size={10} /> Encerrada
                                                    </span>
                                                )}
                                            </div>

                                            <div className="chat-messages-feed" ref={chatScrollRef}>
                                                {loadingChat ? (
                                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 20, color: '#94a3b8' }}><FaSpinner className="go-spin-icon"/> &nbsp;Buscando conversas...</div>
                                                ) : chatMessages.length === 0 ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 40, color: '#94a3b8', gap: 8 }}>
                                                        <FaComments size={32} style={{ opacity: 0.3 }} />
                                                        <span>Nenhuma conversa com este parceiro neste canal ainda.</span>
                                                        <small>{canal === CANAL_WHATSAPP ? 'Dispare um WhatsApp de cotação para iniciar.' : 'Envie um e-mail de cotação para iniciar.'}</small>
                                                    </div>
                                                ) : chatMessages.map(msg => (
                                                    <div key={msg.id} className={`chat-bubble-wrapper ${msg.tipo === 'enviada' ? 'sent' : 'received'}`}>
                                                        {msg.tipo === 'recebida' && (
                                                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#475569', flexShrink: 0 }}>
                                                                {activeOfficeData?.nome_fantasia?.charAt(0) || 'P'}
                                                            </div>
                                                        )}
                                                        <div className="chat-bubble">
                                                            {msg.tipo === 'recebida' && (
                                                                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>{activeOfficeData?.nome_fantasia}</div>
                                                            )}
                                                            <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{msg.mensagem}</p>
                                                            {msg.anexos?.length > 0 && (
                                                                <div style={{ marginTop: 6 }}>
                                                                    {msg.anexos.map((a, i) => <AnexoChip key={i} anexo={a} />)}
                                                                </div>
                                                            )}
                                                            <div className="chat-meta">
                                                                <span>{new Date(msg.data_hora).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                {msg.tipo === 'enviada' && <EmailTick status={msg.status_leitura} />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {!isClosed ? (
                                                <div style={{ borderTop: '1px solid #e2e8f0' }}>
                                                    {canal === CANAL_EMAIL && chatAttachments.length > 0 && (
                                                        <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 4, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                            {chatAttachments.map((f, i) => (
                                                                <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#e0f2fe', borderRadius: 6, padding: '2px 8px', fontSize: '0.75rem', color: '#0284c7' }}>
                                                                    <FaPaperclip size={10} /> {f.name}
                                                                    <button onClick={() => setChatAttachments(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0284c7', padding: 0, marginLeft: 2 }}>
                                                                        <FaTimes size={10} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="chat-reply-box">
                                                        {canal === CANAL_EMAIL && (
                                                            <>
                                                                <input type="file" multiple ref={chatFileInputRef} style={{ display: 'none' }} onChange={e => { if (e.target.files?.length) setChatAttachments(prev => [...prev, ...Array.from(e.target.files)]); }}/>
                                                                <button title="Anexar" onClick={() => chatFileInputRef.current.click()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 8px', fontSize: '1.1rem', flexShrink: 0 }}>
                                                                    <FaPaperclip />
                                                                </button>
                                                            </>
                                                        )}
                                                        {canal === CANAL_WHATSAPP && (
                                                            <span title="WhatsApp" style={{ color: '#16a34a', padding: '0 8px', fontSize: '1.1rem', flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                                                                <FaWhatsapp />
                                                            </span>
                                                        )}
                                                        <textarea
                                                            rows={2}
                                                            placeholder={canal === CANAL_WHATSAPP ? 'Mensagem de WhatsApp... (Enter para enviar)' : 'Digite sua resposta... (Enter para enviar)'}
                                                            value={replyMessage}
                                                            onChange={e => setReplyMessage(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendIndividualReply(); } }}
                                                        />
                                                        <button
                                                            className="go-btn-new primary"
                                                            onClick={handleSendIndividualReply}
                                                            disabled={sendingReply || (!replyMessage.trim() && (canal === CANAL_WHATSAPP || chatAttachments.length === 0))}
                                                            style={canal === CANAL_WHATSAPP ? { background: '#16a34a', borderColor: '#16a34a' } : undefined}
                                                        >
                                                            {sendingReply ? <FaSpinner className="go-spin-icon"/> : <FaPaperPlane />}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ padding: '12px 16px', background: '#fef2f2', borderTop: '1px solid #fca5a5', display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', fontSize: '0.85rem' }}>
                                                    <FaLock /> Cotação encerrada — resposta desabilitada.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── DECISÕES ── */}
                        {activeTab === 'actions' && (
                            <div className="cockpit-content go-animate-up">
                                <h3 style={{ marginBottom: 20 }}>Painel de Decisão</h3>
                                <div className="action-grid">

                                    {/* ── APROVAR ORÇAMENTO ── abre wizard */}
                                    <div className="big-action-card approve">
                                        <div className="icon-area"><FaCheckCircle/></div>
                                        <div className="text-area">
                                            <strong>Aprovar Orçamento</strong>
                                            <small>Selecione o fornecedor e libere a execução.</small>
                                            <button
                                                className="btn-action-trigger success"
                                                onClick={handleOpenApprovalWizard}
                                                disabled={isClosed}
                                                style={{ marginTop: 10 }}
                                            >
                                                <FaCheckCircle style={{ marginRight: 6 }} />
                                                Iniciar Aprovação
                                            </button>
                                            {isClosed && (
                                                <small style={{ color: '#94a3b8', marginTop: 4, display: 'block' }}>
                                                    Cotação encerrada
                                                </small>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── FINALIZAR & ANEXAR NF ── layout corrigido */}
                                    {ticket.status === 'aprovado' && (
                                        <div className="big-action-card finish">
                                            <div className="icon-area"><FaFileInvoiceDollar/></div>
                                            <div className="text-area">
                                                <strong>Finalizar & Anexar NF</strong>
                                                <small>Encerra o chamado e arquiva a nota fiscal.</small>

                                                {/* ✨ Upload zone customizado — sem overflow */}
                                                <input
                                                    id="nf-file-hidden"
                                                    type="file"
                                                    accept=".pdf,.png,.jpg,.jpeg"
                                                    ref={nfFileInputRef}
                                                    style={{ display: 'none' }}
                                                    onChange={e => setNfArquivo(e.target.files[0] || null)}
                                                />
                                                <label
                                                    htmlFor="nf-file-hidden"
                                                    className={`nf-upload-zone ${nfArquivo ? 'nf-has-file' : ''}`}
                                                >
                                                    {nfArquivo ? (
                                                        <>
                                                            <FaFilePdf className="nf-upload-file-icon" />
                                                            <div className="nf-upload-info">
                                                                <span className="nf-upload-name">{nfArquivo.name}</span>
                                                                <small>{(nfArquivo.size / 1024).toFixed(0)} KB</small>
                                                            </div>
                                                            <FaCheck className="nf-upload-ok" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaPaperclip className="nf-upload-clip" />
                                                            <div className="nf-upload-info">
                                                                <span>Clique para selecionar</span>
                                                                <small>PDF, PNG ou JPG</small>
                                                            </div>
                                                        </>
                                                    )}
                                                </label>

                                                {nfArquivo && (
                                                    <button
                                                        type="button"
                                                        className="nf-remove-btn"
                                                        onClick={e => { e.preventDefault(); setNfArquivo(null); if (nfFileInputRef.current) nfFileInputRef.current.value = ''; }}
                                                    >
                                                        <FaTimes size={10} /> Remover arquivo
                                                    </button>
                                                )}

                                                <button
                                                    className={`btn-action-trigger dark ${!nfArquivo ? 'btn-trigger-disabled' : ''}`}
                                                    onClick={handleConclude}
                                                    disabled={!nfArquivo}
                                                >
                                                    <FaCheckDouble style={{ marginRight: 6 }} />
                                                    Concluir Chamado
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── EM ANÁLISE ── */}
                                    <div className="big-action-card analyze">
                                        <div className="icon-area"><FaSearch/></div>
                                        <div className="text-area">
                                            <strong>Em Análise</strong>
                                            <small>Investigar melhor o problema.</small>
                                            <button className="btn-action-trigger info" onClick={handleAnalyze}>Analisar</button>
                                        </div>
                                    </div>

                                    {/* ── PENDENTE ── */}
                                    <div className="big-action-card pending">
                                        <div className="icon-area"><FaPauseCircle/></div>
                                        <div className="text-area">
                                            <strong>Deixar Pendente</strong>
                                            <small>Aguardar mais informações.</small>
                                            <button className="btn-action-trigger warning" onClick={handlePending}>Suspender</button>
                                        </div>
                                    </div>

                                    {/* ── REPROVAR ── */}
                                    <div className="big-action-card reject">
                                        <div className="icon-area"><FaBan/></div>
                                        <div className="text-area">
                                            <strong>Reprovar / Cancelar</strong>
                                            <small>Cancela sem custos.</small>
                                            <button className="btn-action-trigger danger" onClick={handleReject}>Reprovar</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── CAMPOS EXTRAS ── */}
        {activeTab === 'campos_extras' && (
            <CamposExtrasTab ticket={ticket} />
        )}

                        {/* ── LOGS ── */}
                        {activeTab === 'logs' && (
                            <div className="cockpit-content go-animate-up">
                                <h3><FaClock /> Linha do Tempo da Manutenção</h3>
                                <div className="logs-timeline">
                                    {loadingLogs ? (
                                        <div style={{ padding: 20, color: '#94a3b8' }}><FaSpinner className="go-spin-icon"/> Carregando...</div>
                                    ) : logsAuditoria.length === 0 ? (
                                        <div style={{ padding: 20, color: '#94a3b8' }}>Nenhum log registrado.</div>
                                    ) : logsAuditoria.map(log => (
                                        <div key={log.id} className="log-item">
                                            <div className="log-icon"><FaCheckCircle color="var(--go-primary)"/></div>
                                            <div className="log-content">
                                                <div className="log-header">
                                                    <strong>{log.acao}</strong>
                                                    <span className="log-date">{new Date(log.data_hora).toLocaleString()}</span>
                                                </div>
                                                <p>{log.detalhe}</p>
                                                <small>Usuário: <strong>{log.usuario_nome}</strong></small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════════════════════
                APPROVAL WIZARD — aparece por cima do modal principal
                ════════════════════════════════════════════════════════ */}
            {approvalWizardOpen && (
                <ApprovalWizard
                    ticket={ticket}
                    oficinas={safeOficinas}
                    onClose={() => setApprovalWizardOpen(false)}
                    onConfirmed={handleApproveConfirmed}
                />
            )}

            <style>{`
                /* ── ANIMAÇÕES GERAIS ── */
                @keyframes go-spin    { to { transform: rotate(360deg); } }
                @keyframes mam-pulse  { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
                @keyframes mam-flash  { 0% { background:#fffbeb;border-color:#f59e0b; } 50% { background:#fef3c7;border-color:#d97706; } 100% { background:#f8fafc;border-color:transparent; } }
                @keyframes apw-fade   { from { opacity:0; } to { opacity:1; } }
                @keyframes apw-scale  { from { opacity:0; transform:scale(0.92) translateY(16px); } to { opacity:1; transform:none; } }

                /* ── BADGES ── */
                .mam-unread-badge {
                    background:#ef4444; color:#fff; border-radius:12px; min-width:20px; height:20px;
                    padding:0 6px; display:inline-flex; align-items:center; justify-content:center;
                    font-size:0.7rem; font-weight:800; flex-shrink:0; animation:mam-pulse 2s infinite;
                }
                .mam-unread-tab-badge {
                    background:#ef4444; color:#fff; border-radius:50%; width:20px; height:20px;
                    display:inline-flex; align-items:center; justify-content:center;
                    font-size:0.65rem; font-weight:800; margin-left:6px; animation:mam-pulse 1.5s infinite;
                }
                .qs-item.has-unread { border-left:3px solid #ef4444 !important; background:#fff5f5 !important; }
                .qs-item.new-message-flash { animation:mam-flash 0.8s ease 3; }
                .qs-item.has-unread strong { color:#0f172a; font-weight:700; }

                /* ── SUB-TABS DE CANAL (E-mail | WhatsApp) ── */
                .mam-canal-switch {
                    display:flex; gap:5px; padding:4px; margin-bottom:12px;
                    background:#f1f5f9; border:1px solid #e2e8f0; border-radius:11px;
                }
                .mam-canal-pill {
                    flex:1; display:inline-flex; align-items:center; justify-content:center; gap:7px;
                    padding:8px 10px; border:none; border-radius:8px; cursor:pointer;
                    background:transparent; color:#64748b; font-weight:700; font-size:0.82rem;
                    font-family:inherit; transition:all .18s;
                }
                .mam-canal-pill svg { font-size:0.95rem; }
                .mam-canal-pill:hover { color:#334155; }
                .mam-canal-pill.active.email    { background:#fff; color:#2563eb; box-shadow:0 1px 4px rgba(15,23,42,.12); }
                .mam-canal-pill.active.whatsapp { background:#fff; color:#16a34a; box-shadow:0 1px 4px rgba(15,23,42,.12); }

                /* Aviso de parceiros sem o contato do canal */
                .mam-canal-warn {
                    display:flex; align-items:center; gap:8px; margin-top:10px;
                    padding:8px 11px; background:#fffbeb; border:1px solid #fde68a;
                    border-radius:8px; font-size:0.75rem; color:#78350f; line-height:1.35;
                }
                .mam-canal-warn svg { color:#d97706; flex-shrink:0; }
                .mam-canal-warn strong { font-weight:800; }

                /* Item da sidebar inelegível no canal */
                .qs-item.mam-qs-disabled { opacity:0.6; }
                .qs-item.mam-qs-disabled .qs-checkbox { cursor:not-allowed; opacity:0.5; }

                /* ── CARD TEXTO — overflow fix ── */
                .big-action-card .text-area {
                    min-width: 0;
                    overflow: hidden;
                    flex: 1;
                }
                .big-action-card .text-area strong {
                    display: block;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .big-action-card .text-area small {
                    display: block;
                    margin-bottom: 6px;
                    line-height: 1.4;
                    white-space: normal;
                }
                .btn-trigger-disabled { opacity: 0.45; cursor: not-allowed !important; }

                /* ── NF UPLOAD ZONE ── */
                .nf-upload-zone {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 12px;
                    border: 2px dashed #cbd5e1;
                    border-radius: 10px;
                    cursor: pointer;
                    background: #f8fafc;
                    transition: all 0.2s;
                    margin: 8px 0 4px;
                    overflow: hidden;
                    min-width: 0;
                }
                .nf-upload-zone:hover {
                    border-color: #64748b;
                    background: #f1f5f9;
                }
                .nf-has-file {
                    border-color: #10b981;
                    border-style: solid;
                    background: #f0fdf4;
                }
                .nf-upload-clip  { color: #94a3b8; flex-shrink: 0; font-size: 1rem; }
                .nf-upload-file-icon { color: #ef4444; flex-shrink: 0; font-size: 1rem; }
                .nf-upload-ok    { color: #10b981; flex-shrink: 0; font-size: 0.85rem; }
                .nf-upload-info  { flex: 1; display: flex; flex-direction: column; min-width: 0; gap: 1px; }
                .nf-upload-info span { font-size: 0.8rem; font-weight: 600; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .nf-upload-info small { font-size: 0.68rem; color: #94a3b8; }
                .nf-upload-name { font-size: 0.78rem !important; }
                .nf-remove-btn {
                    background: none; border: none; cursor: pointer;
                    font-size: 0.72rem; color: #94a3b8; display: inline-flex;
                    align-items: center; gap: 4px; padding: 0; margin-bottom: 6px;
                    font-family: inherit; transition: color 0.15s;
                }
                .nf-remove-btn:hover { color: #ef4444; }

                /* ════════════════════════════════
                   APPROVAL WIZARD
                   ════════════════════════════════ */
                .apw-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.65);
                    backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10001;
                    animation: apw-fade 0.2s ease;
                }
                .apw-card {
                    background: #ffffff;
                    border-radius: 18px;
                    width: 480px;
                    max-width: 92vw;
                    max-height: 88vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 32px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.05);
                    overflow: hidden;
                    animation: apw-scale 0.28s cubic-bezier(0.34,1.2,0.64,1);
                }

                /* Progress bar */
                .apw-progress-bar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px 32px 4px;
                    gap: 0;
                }
                .apw-step-dot {
                    width: 30px; height: 30px;
                    border-radius: 50%;
                    background: #e2e8f0; color: #94a3b8;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.8rem; font-weight: 800;
                    flex-shrink: 0;
                    transition: all 0.3s;
                    border: 2px solid #e2e8f0;
                }
                .apw-step-dot.active {
                    background: #2563eb; color: white;
                    border-color: #2563eb;
                    box-shadow: 0 0 0 4px rgba(37,99,235,0.15);
                }
                .apw-step-dot.done {
                    background: #10b981; color: white;
                    border-color: #10b981;
                }
                .apw-step-line {
                    flex: 1; height: 2px;
                    background: #e2e8f0;
                    transition: background 0.4s;
                    margin: 0 4px;
                }
                .apw-step-line.done { background: #10b981; }

                /* Header */
                .apw-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 18px 24px 14px;
                }
                .apw-header-icon {
                    width: 40px; height: 40px;
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                    flex-shrink: 0;
                }
                .apw-header-icon.blue   { background: #eff6ff; color: #2563eb; }
                .apw-header-icon.amber  { background: #fffbeb; color: #d97706; }
                .apw-header-icon.red    { background: #fef2f2; color: #dc2626; }
                .apw-header h3 { margin: 0 0 3px; font-size: 1.1rem; color: #0f172a; font-weight: 800; }
                .apw-header p  { margin: 0; font-size: 0.82rem; color: #64748b; }
                .danger-bg { background: #fff5f5; border-bottom: 1px solid #fee2e2; }
                .danger-bg h3 { color: #7f1d1d !important; }

                /* Body */
                .apw-body {
                    padding: 0 24px 16px;
                    flex: 1;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .apw-body::-webkit-scrollbar { width: 4px; }
                .apw-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

                .apw-field-label {
                    font-size: 0.72rem;
                    font-weight: 800;
                    color: #475569;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-bottom: 2px;
                }

                /* Busca */
                .apw-search-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0 12px;
                    transition: border-color 0.2s;
                }
                .apw-search-box:focus-within { border-color: #2563eb; background: white; }
                .apw-search-box svg { color: #94a3b8; font-size: 12px; flex-shrink: 0; }
                .apw-search-box input {
                    border: none; background: transparent; outline: none;
                    padding: 9px 0; font-size: 0.88rem; width: 100%; color: #334155;
                    font-family: inherit;
                }

                /* Lista de oficinas */
                .apw-oficinas-list {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    overflow: hidden;
                    max-height: 180px;
                    overflow-y: auto;
                }
                .apw-oficinas-list::-webkit-scrollbar { width: 4px; }
                .apw-oficinas-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
                .apw-oficinas-empty {
                    padding: 20px;
                    text-align: center;
                    color: #94a3b8;
                    font-size: 0.85rem;
                    font-style: italic;
                }
                .apw-oficina-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 14px;
                    cursor: pointer;
                    transition: background 0.15s;
                    border-bottom: 1px solid #f8fafc;
                }
                .apw-oficina-item:last-child { border-bottom: none; }
                .apw-oficina-item:hover { background: #f8fafc; }
                .apw-oficina-item.selected { background: #eff6ff; }
                .apw-radio-btn {
                    width: 18px; height: 18px;
                    border-radius: 50%;
                    border: 2px solid #cbd5e1;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                    transition: border-color 0.15s;
                }
                .apw-oficina-item.selected .apw-radio-btn { border-color: #2563eb; }
                .apw-radio-dot {
                    width: 8px; height: 8px;
                    border-radius: 50%;
                    background: #2563eb;
                }
                .apw-oficina-info { flex: 1; min-width: 0; }
                .apw-oficina-info strong { display: block; font-size: 0.88rem; color: #1e293b; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .apw-oficina-info span  { display: block; font-size: 0.74rem; color: #94a3b8; }

                /* Valor input */
                .apw-valor-input {
                    display: flex;
                    align-items: stretch;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 10px;
                    overflow: hidden;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .apw-valor-input:focus-within {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
                }
                .apw-valor-prefix {
                    padding: 11px 14px;
                    background: #f8fafc;
                    border-right: 1px solid #e2e8f0;
                    font-weight: 800;
                    color: #475569;
                    font-size: 0.95rem;
                    display: flex;
                    align-items: center;
                }
                .apw-valor-input input {
                    flex: 1;
                    border: none;
                    padding: 11px 14px;
                    font-size: 1.15rem;
                    font-weight: 700;
                    color: #0f172a;
                    outline: none;
                    background: transparent;
                    font-family: inherit;
                }

                /* Summary card */
                .apw-summary-card {
                    background: #f8fafc;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 12px;
                    overflow: hidden;
                }
                .apw-summary-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 16px;
                    border-bottom: 1px solid #f1f5f9;
                    gap: 12px;
                }
                .apw-summary-row:last-child { border-bottom: none; }
                .apw-summary-row span  { font-size: 0.8rem; color: #64748b; flex-shrink: 0; }
                .apw-summary-row strong { font-size: 0.88rem; font-weight: 700; color: #1e293b; text-align: right; }
                .apw-summary-row.highlight { background: #f0fdf4; }
                .apw-summary-row.highlight strong { color: #16a34a; font-size: 1rem; font-weight: 800; }

                /* Alerts */
                .apw-alert {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px 14px;
                    border-radius: 10px;
                    font-size: 0.83rem;
                    line-height: 1.55;
                }
                .apw-alert p { margin: 0; }
                .apw-alert.amber {
                    background: #fffbeb;
                    border: 1px solid #fde68a;
                    color: #78350f;
                }
                .apw-alert.amber svg { color: #d97706; margin-top: 2px; flex-shrink: 0; }
                .apw-alert.danger {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #7f1d1d;
                }
                .apw-alert.danger svg { color: #dc2626; margin-top: 2px; flex-shrink: 0; }

                /* Final box */
                .apw-final-box {
                    background: #fef2f2;
                    border: 1.5px solid #fecaca;
                    border-radius: 12px;
                    padding: 18px;
                }
                .apw-final-title {
                    font-size: 0.82rem;
                    font-weight: 800;
                    color: #991b1b;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                    margin-bottom: 12px;
                }
                .apw-final-list {
                    list-style: none;
                    padding: 0; margin: 0;
                    display: flex; flex-direction: column; gap: 8px;
                }
                .apw-final-list li {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 12px;
                    background: rgba(255,255,255,0.65);
                    border-radius: 8px;
                    font-size: 0.875rem;
                    color: #7f1d1d;
                    font-weight: 500;
                }
                .apw-check-icon {
                    color: #ef4444;
                    font-weight: 900;
                    flex-shrink: 0;
                }

                /* Footer */
                .apw-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 14px 24px;
                    background: #f8fafc;
                    border-top: 1px solid #e2e8f0;
                    gap: 10px;
                    flex-shrink: 0;
                }
                .danger-footer { background: #fff5f5; border-top-color: #fee2e2; }

                /* Botões do wizard */
                .apw-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 10px 20px;
                    border-radius: 9px;
                    font-weight: 700;
                    font-size: 0.88rem;
                    cursor: pointer;
                    border: 1.5px solid transparent;
                    transition: all 0.2s;
                    font-family: inherit;
                    white-space: nowrap;
                }
                .apw-btn.ghost {
                    background: transparent;
                    color: #64748b;
                    border-color: #e2e8f0;
                }
                .apw-btn.ghost:hover { background: #f1f5f9; border-color: #cbd5e1; }
                .apw-btn.primary {
                    background: #2563eb; color: white; border-color: #2563eb;
                }
                .apw-btn.primary:hover:not(:disabled) { background: #1d4ed8; }
                .apw-btn.primary:disabled { background: #cbd5e1; border-color: #cbd5e1; cursor: not-allowed; }
                .apw-btn.amber {
                    background: #f59e0b; color: white; border-color: #f59e0b;
                }
                .apw-btn.amber:hover { background: #d97706; }
                .apw-btn.danger {
                    background: #ef4444; color: white; border-color: #ef4444;
                    padding: 12px 24px;
                    font-size: 0.92rem;
                }
                .apw-btn.danger:hover:not(:disabled) {
                    background: #b91c1c;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(239,68,68,0.35);
                }

                .cockpit-body-wrapper {
    display: flex;
    flex-direction: column;
    overflow: hidden;  /* o scroll fica dentro do ce-scroll, não aqui */
}
 
/*
  Quando a aba ativa for campos_extras, o ce-root precisa
  se esticar até preencher o wrapper.
  Como o wrapper já é flex-column, basta garantir flex:1 no filho direto.
*/
.cockpit-body-wrapper > .ce-root {
    flex: 1;
    min-height: 0;
}
                .apw-btn.danger:disabled { opacity: 0.55; cursor: not-allowed; transform: none; box-shadow: none; }
            `}</style>
        </>
    );
}